import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, GraduationCap, Search, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { telecharger } from '../components/qualiopi/shared';
import { Input } from '../components/ui/input';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { LigneFormateurAdmin } from '../components/administratif/LigneFormateurAdmin';

const Chiffre = ({ valeur, label, testId }) => (
  <div className="rounded-xl bg-white/10 px-4 py-3">
    <p className="text-2xl font-bold" data-testid={testId}>{valeur}</p>
    <p className="text-xs text-white/70">{label}</p>
  </div>
);

const FILTRES = [['tous', 'Tous'], ['non_signee', 'Charte non signée']];

export default function AdministratifFormateurs() {
  const [data, setData] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const { previewDocument, DocumentModal } = useDocumentPreview();
  useEffect(() => {
    axios.get('/charte-formateurs/suivi').then(({ data: d }) => setData(d)).catch(() => setData({ erreur: true }));
  }, []);

  if (!data) return <div className="p-8 text-slate-500" data-testid="admin-formateurs-chargement">Chargement…</div>;
  if (data.erreur) return <div className="p-8 text-red-600" data-testid="admin-formateurs-erreur">Accès non autorisé ou erreur de chargement.</div>;

  const c = data.compteurs;
  const q = recherche.trim().toLowerCase();
  const liste = data.formateurs.filter((f) => (filtre === 'tous' || !f.charte) && `${f.nom} ${f.prenom} ${f.email}`.toLowerCase().includes(q));

  return (
    <div className="space-y-6" data-testid="admin-formateurs-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]" data-testid="admin-formateurs-retour">
        <ArrowLeft className="h-4 w-4" /> Tableau de bord
      </Link>
      <header className="rounded-2xl bg-[#334155] p-6 sm:p-8 text-white shadow-sm">
        <div className="flex items-center gap-4">
          <div className="rounded-xl bg-white/15 p-3"><GraduationCap className="h-7 w-7" /></div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Formateurs</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-white/70">Chartes acceptées, lettres de mission signées (indicateur 21) et conformité des intervenants (indicateur 27) — preuves Qualiopi, aussi incluses dans le dossier ZIP.</p>
        <button type="button" onClick={() => telecharger('/qualiopi/sous-traitants/pdf', 'Conformite_intervenants.pdf').catch(() => toast.error('PDF indisponible'))}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/25" data-testid="admin-formateurs-pdf-conformite">
          <FileDown className="h-3.5 w-3.5" /> Tableau PDF conformité (ind. 27)
        </button>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-xl">
          <Chiffre valeur={c.formateurs} label="Formateurs" testId="admin-formateurs-total" />
          <Chiffre valeur={`${c.chartes_signees}/${c.formateurs}`} label="Chartes acceptées" testId="admin-formateurs-chartes" />
          <Chiffre valeur={c.lettres_signees} label="Lettres signées" testId="admin-formateurs-lettres" />
        </div>
      </header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher un formateur" className="pl-9" data-testid="admin-formateurs-recherche" />
        </div>
        <div className="flex gap-2">
          {FILTRES.map(([cle, label]) => (
            <button key={cle} type="button" onClick={() => setFiltre(cle)} data-testid={`admin-formateurs-filtre-${cle}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${filtre === cle ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3" data-testid="admin-formateurs-liste">
        {liste.map((f) => <LigneFormateurAdmin key={f.user_id} f={f} onPdf={previewDocument} />)}
        {liste.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="admin-formateurs-vide">Aucun formateur</p>}
      </div>
      <DocumentModal />
    </div>
  );
}
